.root {
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
}

/* ─── Controls: view switch, topic filter and count left; legend and export right ─── */
.controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space__sm) var(--space__lg);
}

.controlsStart,
.controlsEnd {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space__sm) var(--space__md);
}

.count {
  font-size: var(--font-size__sm);
  line-height: var(--line-height__xs);
  color: var(--color__gray-600);
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space__md);
}

.legendItem {
  display: flex;
  align-items: center;
  gap: var(--space__xs);
  font-size: var(--font-size__sm);
  line-height: var(--line-height__xs);
  color: var(--color__gray-600);
  white-space: nowrap;
}

/* ─── The export's own header, in place of the controls ─── */
.staticHeader {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space__sm) var(--space__lg);
}

.staticTitle {
  display: flex;
  flex-direction: column;
  gap: var(--space__xs);
}

.entityName {
  font-size: var(--font-size__lg);
  font-weight: var(--font-weight__semibold);
  line-height: var(--line-height__md);
  color: var(--color__gray-900);
}

/* ─── Valence swatch ─── */
.swatch {
  width: 10px;
  height: 10px;
  border-radius: var(--border-radius__xs);
  flex-shrink: 0;
}

.positive {
  background: var(--valence__positive-strong);
}

.negative {
  background: var(--valence__negative-strong);
}

.positive-and-negative {
  background: linear-gradient(
    -45deg,
    var(--valence__negative-strong) 0 48%,
    var(--color__white) 48% 52%,
    var(--valence__positive-strong) 52% 100%
  );
}

.swatchSpacer {
  width: 10px;
  flex-shrink: 0;
}

/* ─── Grid: axis title, level labels, then the cells ─── */
.matrix {
  display: grid;
}

/* The impact levels stand on their side, so their column is one line of text wide. */
.topics {
  grid-template-columns: var(--space__lg) max-content repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(2, auto) var(--space__xl) var(--space__lg);
}

.matters {
  grid-template-columns: var(--space__lg) max-content repeat(3, minmax(0, 1fr));
  grid-template-rows: repeat(3, auto) var(--space__xl) var(--space__lg);
}

/* Both axis titles and the impact levels read upwards, as on the matrix they replace. */
.yAxis {
  grid-column: 1;
  grid-row: 1 / -3;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  text-align: center;
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__semibold);
  color: var(--color__gray-600);
}

.yLevel {
  grid-column: 2;
  align-self: center;
  justify-self: center;
  padding-inline: var(--space__xs);
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  text-align: center;
  font-size: var(--font-size__sm);
  line-height: var(--line-height__xs);
  color: var(--color__gray-600);
}

.xLevel {
  grid-row: -3;
  align-self: end;
  text-align: center;
  font-size: var(--font-size__sm);
  line-height: var(--line-height__xs);
  color: var(--color__gray-600);
}

.xAxis {
  grid-row: -2;
  grid-column: 3 / -1;
  align-self: end;
  text-align: center;
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__semibold);
  line-height: var(--line-height__xs);
  color: var(--color__gray-600);
}

.cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space__sm);
  min-width: 0;
  padding: var(--space__md);
  border-right: 1px solid var(--color__gray-200);
  border-bottom: 1px solid var(--color__gray-200);
  background: var(--color__white);
}

.topics .cell {
  min-height: 120px;
}

.matters .cell {
  min-height: 96px;
}

.firstRow {
  border-top: 1px solid var(--color__gray-200);
}

.firstCol {
  border-left: 1px solid var(--color__gray-200);
}

.topLeft {
  border-top-left-radius: var(--border-radius__lg);
}

.topRight {
  border-top-right-radius: var(--border-radius__lg);
}

.bottomLeft {
  border-bottom-left-radius: var(--border-radius__lg);
}

.bottomRight {
  border-bottom-right-radius: var(--border-radius__lg);
}

/* Material on neither axis: the quiet corner. */
.quiet {
  background: var(--color__gray-50);
}

/* ─── Entries ─── */
.item {
  display: flex;
  align-items: flex-start;
  gap: var(--space__sm);
  max-width: 100%;
  font-size: var(--font-size__sm);
  line-height: var(--line-height__xs);
  text-align: left;
  color: var(--color__gray-900);
}

.item .swatch {
  margin-top: var(--space__xs);
}

.itemText {
  min-width: 0;
  overflow-wrap: break-word;
  hyphens: auto;
}

.qualifier {
  color: var(--color__gray-600);
}

.muted {
  color: var(--color__gray-600);
}

/* An entry that opens something: a topic (a button) or a matter (a link). The design-system link
   underlines only pure-text links and draws no focus ring, so both states are drawn here. */
.entryTrigger {
  display: block;
  max-width: 100%;
}

.entryTrigger > :is(a, button) {
  display: block;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  text-align: left;
}

.entryTrigger > :is(a, button):is([data-hovered], [data-focus-visible]) .itemText {
  text-decoration: underline;
}

.entryTrigger > :is(a, button)[data-focus-visible] {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: var(--focus-ring__offset);
  border-radius: var(--border-radius__xs);
}

.lowLow {
  font-size: var(--font-size__sm);
  line-height: var(--line-height__xs);
  color: var(--color__gray-600);
}

/* ─── Topic tooltip ─── */
.tooltip {
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
}

.tooltipTitle {
  font-weight: var(--font-weight__semibold);
}

.tooltipRow {
  display: grid;
  grid-template-columns: 10px 1fr;
  column-gap: var(--space__sm);
}

.tooltipRow .swatch {
  margin-top: var(--space__xs);
}

.tooltipMeta {
  grid-column: 2;
  color: var(--color__gray-600);
}
